import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import moment from 'moment';
import { MenuItem } from 'primeng/api';
import { Observable, take } from 'rxjs';
import { SeminarFilterOptionModel } from '../../../models/seminar.model';
import { UserModel } from '../../../models/user.model';
import { getSeminarFirstDateString } from '../../../utils/seminar.utils';
import { getTableNameValue, getUserFullName } from '../../../utils/user.utils';
import { SelectOption } from '../custom-select/custom-select.component';

export const defaultOptions = {
  PAGE_SIZE: 5,
};

export interface PageData {
  start: number;
  end: number;
}

type FilterValue = string | number | Date | Date[];

export interface TableColumn {
  columnDef: string;
  header: string;
  width?: string; // Column width in %
  headerAlign?: 'center' | 'left' | 'right';
  cellAlign?: 'center' | 'left' | 'right';
  fontWeight?: string;
  sortable?: boolean;
  sortFn?: (a: any, b: any) => number;
  render?: (row: any) => string | null;
  filterValue?: (row: any) => FilterValue;
  onCellClick?: (row: any, column: any) => void;
  tooltip?: (row: any) => TooltipModel;
  avatarUser?: (row: any) => UserModel;
}

export type Filter =
  | {
      filteredColumn: string;
      type: 'seminar';
      selectOptions?: SeminarFilterOptionModel[];
      selectOptionsLoading?: boolean;
      label?: string;
      placeholder?: string;
      value?: any;
    }
  | {
      filteredColumn: string;
      type:
        | 'text'
        | 'date'
        | 'daterange'
        | 'select'
        | 'multiselect'
        | 'checkbox';
      selectOptions?: SelectOption[];
      selectOptionsLoading?: boolean;
      label?: string;
      placeholder?: string;
      value?: any;
    };

export interface TooltipModel {
  position?: 'top' | 'bottom' | 'left' | 'right';
  autoHide?: boolean;
  tooltipText: string;
}

export interface TableHeaderButton {
  label: string;
  icon: string;
  onClick: () => void;
}

export interface ActiveFilterChip {
  filteredColumn: string;
  label: string;
  value: any;
  isMultiValue: boolean;
}

@Component({
  selector: 'app-custom-table',
  templateUrl: './custom-table.component.html',
  styleUrls: ['./custom-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CustomTableComponent implements OnInit, OnChanges {
  private readonly SEARCH_QUERY_PARAM = 'search';
  private readonly FILTERS_QUERY_PARAM = 'tableFilters';
  private currentQueryParams: Record<string, any> = {};

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: Event) {
    if (!this.filters || this.filters.length === 0) return;
    const filterMenu = this.el.nativeElement.querySelector('#filterMenu');
    const filterMenuButton =
      this.el.nativeElement.querySelector('#filterMenuButton');
    const isClickInside = filterMenu?.contains(event.target as HTMLElement);
    const isButtonClicked = filterMenuButton?.contains(
      event.target as HTMLElement
    );

    if (!isClickInside && !isButtonClicked && this.filterMenuOpen) {
      this.filterMenuOpen = false;
    }
  }

  @Input() dataSource$: Observable<any[]> | null = null; // Data to be displayed in the table
  @Input() columns: TableColumn[] = []; // Column configuration (headers, width, formatting)
  @Input() pageSizeOptions: number[] = [5, 10, 25, 50, 100]; // Page size options
  @Input() pageSize: number = 10; // Default page size
  @Input() filters: Filter[] = [];
  @Input() tableHeaderButton: TableHeaderButton;
  @Input() fuzzySearch: boolean = false;
  @Input() isLoading: boolean = false;
  @Input() noDataMessage: string = 'Keine Daten vorhanden.';
  @Input() noDataIcon: string = '';
  @Input() noSearchResultsMessage: string =
    'Es gibt keine Ergebnisse, die mit deinen Suchkriterien übereinstimmen.';
  @Input() noSearchResultsIcon: string = '';
  @Input() headerColor: string = 'var(--general-90)';
  @Input() borderColor: string = 'var(--general-70)';
  @Input() titleSingleResult: string = 'Ergebnis';
  @Input() titleMultipleResults: string = 'Ergebnisse';
  @Input() titleResultSuffix: string = '';
  @Input() tableHeaderDropdownMenu: {
    label: string;
    options: MenuItem[];
    visible: boolean;
  };

  public getUserFullName = getUserFullName;
  public getTableNameValue = getTableNameValue;

  public data: any[] = null;
  public filteredData: any[] = []; // Data after filtering
  public searchedAndFilteredData: any[] = [];
  public currentPage: number = 1; // Current page number
  public searchQuery: string = ''; // Search query string

  // State for sorting
  public sortedColumn: any = null;
  public sortDirection: 'asc' | 'desc' = 'asc';

  public filterMenuOpen = false;
  public dateFrom: string = '';
  public dateTo: string = '';

  public pageSizeSelectOptions: SelectOption[] = [];

  constructor(
    private el: ElementRef,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private router: Router,
    private activatedRoute: ActivatedRoute
  ) {}

  public ngOnInit(): void {
    this.pageSizeSelectOptions = this.pageSizeOptions.map(option => ({
      value: option,
      label: option.toString(),
    }));
    this.activatedRoute.queryParams.subscribe(params => {
      const previousParams = this.currentQueryParams;
      this.currentQueryParams = params;
      if (params['page']) {
        this.currentPage = Number(params['page']);
      } else {
        this.currentPage = 1;
      }
      if (params['pageSize']) {
        this.pageSize = Number(params['pageSize']);
      }
      const searchOrFiltersChanged =
        (previousParams[this.SEARCH_QUERY_PARAM] || null) !==
          (params[this.SEARCH_QUERY_PARAM] || null) ||
        (previousParams[this.FILTERS_QUERY_PARAM] || null) !==
          (params[this.FILTERS_QUERY_PARAM] || null);

      this.restoreSearchAndFiltersFromQuery(params);

      if (searchOrFiltersChanged) {
        this.applyCurrentFiltersAndSearch(true);
      } else if (this.data) {
        this.cdr.markForCheck();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataSource$']) {
      this.dataSource$?.pipe(take(1)).subscribe(data => {
        this.data = [...data]; // save full data for filtering
        this.filteredData = [...data]; // Start with the full data
        this.searchedAndFilteredData = [...data];
        this.restoreSearchAndFiltersFromQuery(this.currentQueryParams);
        this.applyCurrentFiltersAndSearch(false);
        this.cdr.detectChanges();
      });
      if (
        !changes['dataSource$'].firstChange &&
        changes['dataSource$']?.previousValue !== undefined
      ) {
        this.changePage(1);
      }
    }
  }

  get totalPages() {
    return Math.ceil(this.searchedAndFilteredData?.length / this.pageSize);
  }

  // Paginated data after applying sort
  get paginatedData() {
    // Sort data first, then paginate
    const sortedData = this.sortData(this.searchedAndFilteredData);
    return this.paginateData(sortedData);
  }

  public applySearch(resetPage = true) {
    if (resetPage) {
      this.changePage(1);
    }
    let filteredData = [...this.filteredData];
    filteredData = this.applySearchFilters(filteredData);
    this.searchedAndFilteredData = filteredData;
    this.updateQueryParamsWithTableState();
    this.cdr.markForCheck();
  }

  private applySearchFilters(data: any[]): any[] {
    if (!this.fuzzySearch) {
      return this.applyExactSearch(data);
    } else {
      return this.applyFuzzySearch(data);
    }
  }

  private applyExactSearch(data: any[]): any[] {
    const query = this.searchQuery.toLowerCase().trim();
    return data.filter(row =>
      Object.keys(row).some(key =>
        this.containsSearchQuery(
          row[key],
          query,
          this.columns
            .find(column => column.columnDef === key)
            ?.filterValue?.(row)
        )
      )
    );
  }

  private applyFuzzySearch(data: any[]): any[] {
    const searchTerms = this.searchQuery.toLowerCase().trim().split(/\s+/);
    return data.filter(row =>
      searchTerms.every(term =>
        Object.keys(row).some(key =>
          this.containsSearchQuery(
            row[key],
            term,
            this.columns
              .find(column => column.columnDef === key)
              ?.filterValue?.(row)
          )
        )
      )
    );
  }

  private containsSearchQuery(
    val: any,
    query: string,
    filterValue?: FilterValue
  ): boolean {
    if (filterValue && typeof filterValue === 'string') {
      return filterValue.toLowerCase().includes(query);
    }
    if (filterValue && typeof filterValue === 'number') {
      return filterValue.toString().toLowerCase().includes(query);
    }
    if (Array.isArray(val)) {
      return val.some(item => this.containsSearchQuery(item, query));
    }
    if (typeof val === 'object' && val !== null) {
      if (moment.isDate(val)) {
        return moment(val).format('DD.MM.YYYY').toLowerCase().includes(query);
      }
      return Object.values(val).some(nestedVal =>
        this.containsSearchQuery(nestedVal, query)
      );
    }
    return val?.toString().toLowerCase().includes(query);
  }

  // Pagination logic
  private paginateData(data: any[]) {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return data.slice(start, end);
  }

  // Handle page change (previous/next)
  private changePage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.router.navigate([], {
        queryParams: { page: this.currentPage },
        queryParamsHandling: 'merge',
      });
      this.cdr.markForCheck();
    }
  }

  public prev(): void {
    this.changePage(this.currentPage - 1);
  }

  public next(): void {
    this.changePage(this.currentPage + 1);
  }

  public isFirstPage(): boolean {
    return this.currentPage === 1;
  }

  public isLastPage(): boolean {
    return this.currentPage === this.totalPages;
  }

  // Change page size and reset pagination
  public changePageSize(newSize: string | number): void {
    const newPageSize = Number(newSize);
    const firstItemIndex = (this.currentPage - 1) * this.pageSize;
    this.pageSize = newPageSize;
    this.currentPage = Math.floor(firstItemIndex / this.pageSize) + 1;
    this.router.navigate([], {
      queryParams: { page: this.currentPage, pageSize: this.pageSize },
      queryParamsHandling: 'merge',
    });
  }

  // Get column width (if specified)
  public getColumnWidth(column: any): string {
    return column.width ? column.width : 'auto';
  }

  // Sorting function
  public sortData(data: any[]) {
    if (!this.sortedColumn) {
      return data; // No sorting applied
    }
    if (!this.sortDirection) {
      return data; // No direction specified
    }
    const { columnDef, sortFn } = this.sortedColumn;
    const directionMultiplier = this.sortDirection === 'asc' ? 1 : -1;

    return [...data].sort((a, b) => {
      // Custom sort function
      if (sortFn) {
        return directionMultiplier * sortFn(a, b);
      }
      // Default sort: based on value in column
      const valueA = a[columnDef];
      const valueB = b[columnDef];

      if (valueA == null) return -1 * directionMultiplier; // Handle null or undefined
      if (valueB == null) return 1 * directionMultiplier;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return directionMultiplier * (valueA - valueB);
      }
      return (
        directionMultiplier * valueA.toString().localeCompare(valueB.toString())
      );
    });
  }

  // Function to handle sorting when a column header is clicked
  public onSort(column: any) {
    if (!column.sortable) return; // Skip non-sortable columns

    if (this.sortedColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : null; // Toggle direction
    } else {
      this.sortedColumn = column;
      this.sortDirection = 'asc'; // Start with ascending
    }
  }

  public getTableTitle(amount: number): string {
    if (this.isLoading) {
      return `${this.titleMultipleResults} werden geladen...`;
    }
    if (amount === 0) {
      return `Keine ${this.titleMultipleResults} ${this.titleResultSuffix}`;
    }
    if (amount === 1) {
      return `1 ${this.titleSingleResult} ${this.titleResultSuffix}`;
    }
    return `${amount} ${this.titleMultipleResults} ${this.titleResultSuffix}`;
  }

  get activeFilterChips(): ActiveFilterChip[] {
    if (!this.filters?.length) {
      return [];
    }

    const chips: ActiveFilterChip[] = [];

    for (const filter of this.filters) {
      if (this.isFilterValueEmpty(filter.type, filter.value)) {
        continue;
      }

      const baseLabel = this.getFilterDisplayName(filter);

      if (filter.type === 'multiselect') {
        for (const value of filter.value as any[]) {
          chips.push({
            filteredColumn: filter.filteredColumn,
            label: this.formatChipLabel(
              baseLabel,
              this.getMultiSelectOptionLabel(filter, value)
            ),
            value,
            isMultiValue: true,
          });
        }
        continue;
      }

      if (filter.type === 'seminar') {
        for (const value of filter.value as any[]) {
          chips.push({
            filteredColumn: filter.filteredColumn,
            label: this.formatChipLabel(
              baseLabel,
              this.getSeminarOptionLabel(filter, value)
            ),
            value,
            isMultiValue: true,
          });
        }
        continue;
      }

      if (filter.type === 'select') {
        chips.push({
          filteredColumn: filter.filteredColumn,
          label: this.formatChipLabel(
            baseLabel,
            this.getMultiSelectOptionLabel(filter, filter.value)
          ),
          value: filter.value,
          isMultiValue: false,
        });
        continue;
      }

      if (filter.type === 'date') {
        chips.push({
          filteredColumn: filter.filteredColumn,
          label: this.formatChipLabel(
            baseLabel,
            moment(filter.value).format('DD.MM.YYYY')
          ),
          value: filter.value,
          isMultiValue: false,
        });
        continue;
      }

      if (filter.type === 'daterange') {
        const start = filter.value?.startDate
          ? moment(filter.value.startDate).format('DD.MM.YYYY')
          : '';
        const end = filter.value?.endDate
          ? moment(filter.value.endDate).format('DD.MM.YYYY')
          : '';
        chips.push({
          filteredColumn: filter.filteredColumn,
          label: this.formatChipLabel(baseLabel, `${start} - ${end}`),
          value: filter.value,
          isMultiValue: false,
        });
        continue;
      }

      if (filter.type === 'checkbox') {
        chips.push({
          filteredColumn: filter.filteredColumn,
          label: baseLabel,
          value: filter.value,
          isMultiValue: false,
        });
        continue;
      }

      if (filter.type === 'text') {
        chips.push({
          filteredColumn: filter.filteredColumn,
          label: this.formatChipLabel(baseLabel, String(filter.value)),
          value: filter.value,
          isMultiValue: false,
        });
        continue;
      }
    }

    return chips;
  }

  public removeFilterChip(chip: ActiveFilterChip): void {
    const filter = this.filters.find(
      candidate => candidate.filteredColumn === chip.filteredColumn
    );
    if (!filter) {
      return;
    }

    if (chip.isMultiValue && Array.isArray(filter.value)) {
      filter.value = filter.value.filter(value => value !== chip.value);
    } else {
      filter.value = this.getDefaultFilterValue(filter.type);
    }

    this.applyCurrentFiltersAndSearch(true);
    this.updateQueryParamsWithTableState();
  }

  private getFilterDisplayName(filter: Filter): string {
    if (filter.label) {
      return filter.label;
    }
    const matchingColumn = this.columns.find(
      column => column.columnDef === filter.filteredColumn
    );
    if (matchingColumn?.header) {
      return matchingColumn.header;
    }
    return filter.filteredColumn;
  }

  private formatChipLabel(name: string, value: string): string {
    if (!value) {
      return name;
    }
    return `${name}: ${value}`;
  }

  private getMultiSelectOptionLabel(filter: Filter, value: any): string {
    const options = filter.selectOptions as SelectOption[] | undefined;
    const match = options?.find(option => option.value === value);
    return match?.label ?? String(value);
  }

  private getSeminarOptionLabel(filter: Filter, value: any): string {
    const options = filter.selectOptions as
      | SeminarFilterOptionModel[]
      | undefined;
    const match = options?.find(option => option.id === value);
    if (!match) {
      return String(value);
    }
    const dateString = getSeminarFirstDateString(match);
    return dateString ? `${match.title} (${dateString})` : match.title;
  }

  public onFilteredData(event: any) {
    this.filteredData = event;
    this.applySearch();
  }

  public onFiltersChange(event: any) {
    this.filters = event;
    this.updateQueryParamsWithTableState();
  }

  private applyCurrentFiltersAndSearch(resetPage = false) {
    if (!this.data) {
      return;
    }
    this.filteredData = this.applyFiltersToData(this.data);
    this.applySearch(resetPage);
  }

  private restoreSearchAndFiltersFromQuery(params: Record<string, any>) {
    this.searchQuery = params[this.SEARCH_QUERY_PARAM] || '';

    const serializedFilters = params[this.FILTERS_QUERY_PARAM];
    if (!serializedFilters) {
      this.filters = this.filters.map(filter => ({
        ...filter,
        value: this.getDefaultFilterValue(filter.type),
      }));
      return;
    }

    try {
      const decoded = decodeURIComponent(serializedFilters);
      const parsedFilters = JSON.parse(decoded) as Array<{
        filteredColumn: string;
        type: Filter['type'];
        value: any;
      }>;

      this.filters = this.filters.map(filter => {
        const matchingFilter = parsedFilters.find(
          parsedFilter => parsedFilter.filteredColumn === filter.filteredColumn
        );

        if (!matchingFilter) {
          return {
            ...filter,
            value: this.getDefaultFilterValue(filter.type),
          };
        }

        return {
          ...filter,
          value: this.deserializeFilterValue(filter.type, matchingFilter.value),
        };
      });
    } catch (error) {
      console.error('Could not restore table filters from url', error);
    }
  }

  private updateQueryParamsWithTableState() {
    const searchQuery = this.searchQuery?.trim();
    const filtersPayload = this.filters
      .filter(filter => !this.isFilterValueEmpty(filter.type, filter.value))
      .map(filter => ({
        filteredColumn: filter.filteredColumn,
        type: filter.type,
        value: this.serializeFilterValue(filter.type, filter.value),
      }));

    const nextSearchValue = searchQuery || null;
    const nextFiltersValue =
      filtersPayload.length > 0
        ? encodeURIComponent(JSON.stringify(filtersPayload))
        : null;

    if (
      (this.currentQueryParams[this.SEARCH_QUERY_PARAM] || null) ===
        nextSearchValue &&
      (this.currentQueryParams[this.FILTERS_QUERY_PARAM] || null) ===
        nextFiltersValue
    ) {
      return;
    }

    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [this.SEARCH_QUERY_PARAM]: nextSearchValue,
        [this.FILTERS_QUERY_PARAM]: nextFiltersValue,
      },
      queryParamsHandling: 'merge',
    });
  }

  private isFilterValueEmpty(type: Filter['type'], value: any): boolean {
    if (type === 'daterange') {
      return !value?.startDate || !value?.endDate;
    }
    if (type === 'checkbox') {
      return value === false || value === null || value === undefined;
    }
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    return value === null || value === undefined || value === '';
  }

  private serializeFilterValue(type: Filter['type'], value: any): any {
    if (type === 'daterange') {
      return {
        startDate: value?.startDate
          ? new Date(value.startDate).toISOString()
          : null,
        endDate: value?.endDate ? new Date(value.endDate).toISOString() : null,
      };
    }
    if (type === 'date') {
      return value ? new Date(value).toISOString() : null;
    }
    return value;
  }

  private deserializeFilterValue(type: Filter['type'], value: any): any {
    if (type === 'daterange') {
      return {
        startDate: value?.startDate ? new Date(value.startDate) : null,
        endDate: value?.endDate ? new Date(value.endDate) : null,
      };
    }
    if (type === 'date') {
      return value ? new Date(value) : null;
    }
    return value;
  }

  private getDefaultFilterValue(type: Filter['type']): any {
    if (type === 'daterange') {
      return { startDate: null, endDate: null };
    }
    if (type === 'checkbox') {
      return false;
    }
    return null;
  }

  private applyFiltersToData(data: any[]): any[] {
    return data.filter(item =>
      this.filters.every(filter => {
        const value = filter.value;

        if (filter.type === 'daterange') {
          if (!value?.startDate || !value?.endDate) {
            return true;
          }

          const filterStartDate = new Date(value.startDate);
          const filterEndDate = new Date(value.endDate);
          const itemValue = this.getNestedValue(item, filter.filteredColumn);

          if (Array.isArray(itemValue)) {
            return itemValue.some(entry => {
              if (typeof entry === 'string' || entry instanceof Date) {
                const itemDate = new Date(entry);
                return itemDate >= filterStartDate && itemDate <= filterEndDate;
              }
              if (entry && entry.startDate && entry.endDate) {
                const itemStartDate = new Date(entry.startDate);
                const itemEndDate = new Date(entry.endDate);
                return (
                  itemStartDate <= filterEndDate &&
                  itemEndDate >= filterStartDate
                );
              }
              return false;
            });
          }

          const itemDate = new Date(itemValue);
          return itemDate >= filterStartDate && itemDate <= filterEndDate;
        }

        if (filter.type === 'date') {
          if (!value) {
            return true;
          }

          const selectedDate = new Date(value);
          const itemDate = new Date(
            this.getNestedValue(item, filter.filteredColumn)
          );
          return itemDate.toDateString() === selectedDate.toDateString();
        }

        if (filter.type === 'select') {
          return value
            ? this.getNestedValue(item, filter.filteredColumn) === value
            : true;
        }

        if (filter.type === 'checkbox') {
          const itemValue = this.getNestedValue(item, filter.filteredColumn);
          return value ? true : !itemValue;
        }

        if (filter.type === 'multiselect' || filter.type === 'seminar') {
          if (!value || value.length === 0) {
            return true;
          }

          const itemValue = this.getNestedValue(item, filter.filteredColumn);
          if (Array.isArray(itemValue)) {
            return itemValue.some(entry => value.includes(entry));
          }
          return value.includes(itemValue);
        }

        if (filter.type === 'text') {
          const searchString = value?.toLowerCase();
          const itemString = (
            this.getNestedValue(item, filter.filteredColumn) || ''
          ).toLowerCase();
          return itemString.includes(searchString);
        }

        return true;
      })
    );
  }

  private getNestedValue(obj: any, path: string): any {
    const keys = path.split('.');
    let result = obj;

    for (const key of keys) {
      if (result && Object.prototype.hasOwnProperty.call(result, key)) {
        result = result[key];
      } else {
        return undefined;
      }
    }

    return result;
  }
}
